import React, { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@hobs/components/Dialog';
import { Formik, Form, Field } from 'formik';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import * as Yup from 'yup';

import './CopyServiceRequestsDialog.scss';
import Panel from '@hobs/components/Panel';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import {
  calculateByDays,
  clearCalculateByDays,
  copyServiceRequests,
  getDataForCopySubmit,
  validateServiceRequestsForCopy
} from '@hobs/actions/serviceRequests';
import Loader from '@hobs/components/Loader';
import Toast from '@hobs/components/Toast';
import objectPath from 'object-path';
import { H5 } from '@hobs/components/Headings';
import InfoPanel from '@hobs/components/InfoPanel';
import Money from '@hobs/components/Money';
import DeliveryInformation from '@hobs/components/DeliveryInformation';
import LocationDetails from '@hobs/components/LocationDetails';
import DropZone from '@hobs/components/DropZone';
import Textarea from '@hobs/components/Textarea';
import Tooltip from '@hobs/components/Tooltip';
import { Icon as BpIcon } from '@blueprintjs/core';
import { SERVICE_REQUEST_COPY_VALIDATION_ERRORS } from '@hobs/utils/constants';
import CalculateByDays from '@hobs/utils/calculateByDays';
import { queryLocations } from '@hobs/actions/locations';
import { uploadAttachments } from '@hobs/actions/attachment';
import ShortId from 'shortid';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Confirm from '@hobs/components/Confirm';
import { createLoadingSelector } from '@hobs/utils/loading';
import Dropdown from '@hobs/components/Dropdown';
import InformationToolTip from '@hobs/components/InformationToolTip';
import Input from '@hobs/components/Input';
import File from '@hobs/components/File';

class CopyServiceRequestsDialog extends PureComponent {
  formRef = createRef();
  locationQueryTimeout = null;

  constructor(props) {
    super(props);
    const { t } = props;

    this.state = {
      validatedServiceRequests: [],
      isLoading: false,
      selectedServiceRequestNumber: null,
      forSubmitData: {}
    };

    const customCatalogItemSchema = Yup.object().shape({
      annexId: Yup.string().nullable(),
      name: Yup.string()
        .max(128, 'Service name cannot be longer than 128 symbols')
        .required('Service name is required'),
      description: Yup.string()
        .max(500, 'Service description cannot be longer than 500 symbols')
        .required('Service description is required'),
      unitOfMeasureId: Yup.string()
        .nullable()
        .required(t('UoM is required'))
    });

    const catalogItemSchema = Yup.object().shape({
      unitOfMeasureId: Yup.string()
        .nullable()
        .required(t('UoM is required'))
    });

    this.itemSchema = Yup.object().shape({
      serviceRequest: Yup.object().shape({
        unitId: Yup.string()
          .nullable()
          .required(t('Unit is required')),
        dateFrom: Yup.date()
          .required(t('From date is required'))
          .typeError(t('Invalid date or date format'))
          .nullable(),
        quantity: Yup.number()
          .min(0, t('Quantity must be greater than 0'))
          .required(t('Quantity is required'))
          .typeError(t('Invalid quantity format')),
        location: Yup.object().when(['isAdditionalRequest'], {
          is: true,
          then: Yup.object()
            .required(t('Location is required'))
            .nullable(),
          otherwise: Yup.object().nullable()
        }),
        locationFrom: Yup.object().when(
          ['isAdditionalRequest', 'isTransportation'],
          {
            is: true,
            then: Yup.object()
              .required(t('Location is required'))
              .nullable(),
            otherwise: Yup.object().nullable()
          }
        ),
        comment: Yup.string()
          .max(1500, t('Comment cannot be longer than 1500 symbols'))
          .nullable(),
        generalInformation: Yup.object()
          .shape({
            serialNumber: Yup.string()
              .nullable()
              .max(15, t('Serial number cannot be longer than 15 symbols'))
          })
          .nullable(),
        pickupDetails: Yup.object().when(
          ['isAdditionalRequest', 'isTransportation'],
          {
            is: true,
            then: Yup.object({
              namePoc: Yup.string()
                .nullable()
                .max(256, t('Name POC cannot be longer than 256 symbols'))
                .required(t('Name POC is required')),
              location: Yup.object().shape({
                name: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Place of loading cannot be longer than 256 symbols')
                  )
                  .required(t('Place of loading is required')),
                zipCode: Yup.string()
                  .nullable()
                  .max(5, t('Zip code cannot be longer than 5 symbols')),
                postAddress: Yup.string()
                  .nullable()
                  .max(50, t('Post. add. cannot be longer than 50 symbols')),
                comment: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Location comment cannot be longer than 256 symbols')
                  )
              }),
              mobilePhone: Yup.string()
                .nullable()
                .required(t('Mobile phone is required')),
              email: Yup.string()
                .nullable()
                .email(t('Invalid email format'))
                .required(t('Email is required')),
              mission: Yup.string()
                .nullable()
                .max(256, t('Mission cannot be longer than 256 symbols'))
            }).nullable(),
            otherwise: Yup.object({
              namePoc: Yup.string()
                .nullable()
                .max(256, t('Name POC cannot be longer than 256 symbols')),
              location: Yup.object().shape({
                name: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Place of loading cannot be longer than 256 symbols')
                  ),
                zipCode: Yup.string()
                  .nullable()
                  .max(5, t('Zip code cannot be longer than 5 symbols')),
                postAddress: Yup.string()
                  .nullable()
                  .max(50, t('Post. add. cannot be longer than 50 symbols')),
                comment: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Location comment cannot be longer than 256 symbols')
                  )
              }),
              mobilePhone: Yup.string().nullable(),
              email: Yup.string()
                .nullable()
                .email(t('Invalid email format')),
              mission: Yup.string()
                .nullable()
                .max(256, t('Mission cannot be longer than 256 symbols'))
            }).nullable()
          }
        ),
        deliveryDetails: Yup.object().when(
          ['isAdditionalRequest', 'isTransportation'],
          {
            is: true,
            then: Yup.object({
              namePoc: Yup.string()
                .nullable()
                .max(256, t('Name POC cannot be longer than 256 symbols'))
                .required(t('Name POC is required')),
              location: Yup.object().shape({
                name: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Place of loading cannot be longer than 256 symbols')
                  )
                  .required(t('Place of loading is required')),
                zipCode: Yup.string()
                  .nullable()
                  .max(5, t('Zip code cannot be longer than 5 symbols')),
                postAddress: Yup.string()
                  .nullable()
                  .max(50, t('Post. add. cannot be longer than 50 symbols')),
                comment: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Location comment cannot be longer than 256 symbols')
                  )
              }),
              mobilePhone: Yup.string()
                .nullable()
                .required(t('Mobile phone is required')),
              email: Yup.string()
                .nullable()
                .email(t('Invalid email format'))
                .required(t('Email is required')),
              mission: Yup.string()
                .nullable()
                .max(256, t('Mission cannot be longer than 256 symbols'))
            }).nullable(),
            otherwise: Yup.object({
              namePoc: Yup.string()
                .nullable()
                .max(256, t('Name POC cannot be longer than 256 symbols')),
              location: Yup.object().shape({
                name: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Place of loading cannot be longer than 256 symbols')
                  ),
                zipCode: Yup.string()
                  .nullable()
                  .max(5, t('Zip code cannot be longer than 5 symbols')),
                postAddress: Yup.string()
                  .nullable()
                  .max(50, t('Post. add. cannot be longer than 50 symbols')),
                comment: Yup.string()
                  .nullable()
                  .max(
                    256,
                    t('Location comment cannot be longer than 256 symbols')
                  )
              }),
              mobilePhone: Yup.string().nullable(),
              email: Yup.string()
                .nullable()
                .email(t('Invalid email format')),
              mission: Yup.string()
                .nullable()
                .max(256, t('Mission cannot be longer than 256 symbols'))
            }).nullable()
          }
        ),
        lines: Yup.array()
          .of(
            Yup.object().shape({
              typeId: Yup.string()
                .nullable()
                .required(t('Item type is required'))
                .typeError(t('Item type is required')),
              description: Yup.string()
                .nullable()
                .max(30, t('Description cannot be longer than 30 symbols'))
                .required(t('Description is required')),
              quantity: Yup.number()
                .nullable()
                .min(1, t('Quantity must be greater than 0'))
                .required(t('Quantity is required'))
                .typeError(t('Quantity is required')),
              length: Yup.string().when('typeId', value =>
                getValue(value, '2')
                  ? Yup.number(t('Invalid length format'))
                      .nullable()
                      .min(0.01, t('Length must be greater than 0'))
                      .test('format', t('Invalid length format'), val => {
                        return countDecimals(val) < 3;
                      })
                  : Yup.number(t('Invalid length format'))
                      .test('format', t('Invalid length format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .nullable()
                      .min(0.01, t('Length must be greater than 0'))
                      .required(t('Length is required'))
                      .typeError(t('Invalid length format'))
              ),
              width: Yup.string().when('typeId', value =>
                getValue(value, '2')
                  ? Yup.number(t('Invalid width format'))
                      .test('format', t('Invalid width format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .nullable()
                      .min(0.01, t('Width must be greater than 0'))
                  : Yup.number(t('Invalid width format'))
                      .test('format', t('Invalid width format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .nullable()
                      .min(0.01, t('Width must be greater than 0'))
                      .required(t('Width is required'))
                      .typeError(t('Invalid width format'))
              ),
              height: Yup.string().when('typeId', value =>
                getValue(value, '2')
                  ? Yup.number(t('Invalid height format'))
                      .test('format', t('Invalid height format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .nullable()
                      .min(0.01, t('Height must be greater than 0'))
                  : Yup.number(t('Invalid height format'))
                      .nullable()
                      .test('format', t('Invalid height format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .min(0.01, t('Height must be greater than 0'))
                      .required(t('Height is required'))
                      .typeError(t('Invalid height format'))
              ),
              weight: Yup.string().when('typeId', value =>
                getValue(value, '2')
                  ? Yup.number(t('Invalid weight format'))
                      .test('format', t('Invalid weight format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .nullable()
                      .min(0.01, t('Weight must be greater than 0'))
                  : Yup.number(t('Invalid weight format'))
                      .test('format', t('Invalid weight format'), val => {
                        return countDecimals(val) < 3;
                      })
                      .nullable()
                      .min(0.01, t('Weight must be greater than 0'))
                      .required(t('Weight is required'))
                      .typeError(t('Invalid weight format'))
              ),
              dgrName: Yup.string().when('typeId', value =>
                getValue(value, '2') || getValue(value, '3')
                  ? Yup.string()
                      .nullable()
                      .max(30, t('DGR name cannot be longer than 30 symbols'))
                  : Yup.string()
                      .nullable()
                      .max(30, t('DGR name cannot be longer than 30 symbols'))
                      .required(t('DGR name is required'))
              ),
              unNr: Yup.string().when('typeId', value =>
                getValue(value, '2') || getValue(value, '3')
                  ? Yup.string().nullable()
                  : Yup.string()
                      .nullable()
                      .test(
                        'format',
                        t('Invalid UN-NR format'),
                        val => val && val.toString().length === 4
                      )
                      .required(t('UN-NR is required'))
              ),
              dgrClass: Yup.number().when('typeId', value =>
                getValue(value, '2') || getValue(value, '3')
                  ? Yup.number().nullable()
                  : Yup.number()
                      .typeError(t('DGR class is required'))
                      .nullable()
                      .test('format', t('Invalid DGR class format'), val => {
                        return val > 1 && val < 10 && countDecimals(val) < 3;
                      })
                      .required(t('DGR class is required'))
              ),
              neq: Yup.string().when('typeId', value =>
                !getValue(value, '2') && !getValue(value, '3')
                  ? Yup.number(t('Invalid NEQ format'))
                      .nullable()
                      .test('format', t('Invalid NEQ format'), val => {
                        return val >= 0 && countDecimals(val) < 3;
                      })
                      .min(0, t('NEQ cannot be less than 0'))
                      .required(t('NEQ is required'))
                      .typeError(t('Invalid NEQ format'))
                  : Yup.number().nullable()
              ),
              remarks: Yup.string()
                .nullable()
                .max(50, t('Remarks cannot be longer than 50 symbols'))
            })
          )
          .nullable(),
        isCustom: Yup.boolean(),
        customCatalogItem: Yup.mixed().when('isCustom', {
          is: true,
          then: customCatalogItemSchema,
          otherwise: Yup.mixed().nullable()
        }),
        catalogItem: Yup.mixed().when('isCustom', {
          is: false,
          then: catalogItemSchema,
          otherwise: Yup.mixed().nullable()
        })
      })
    });
    this.validationSchema = Yup.object().shape({
      items: Yup.array()
        .of(this.itemSchema)
        .min(1, t('At least one service request is required'))
    });
  }

  getServiceRequests = () => {
    const {
      exercise,
      partyId,
      serviceRequestIds,
      validateServiceRequestsForCopy,
      onClose
    } = this.props;

    validateServiceRequestsForCopy({
      exerciseId: exercise.exerciseId,
      partyId: partyId,
      serviceRequestIds: serviceRequestIds
    })
      .then(response => {
        if (response && response.success) {
          this.setState({
            validatedServiceRequests: response.payload,
            selectedServiceRequestNumber:
              response.payload[0].serviceRequest.requestNumber
          });
        } else if (response) {
          Toast.warning(response.payload.message);
          onClose();
        }
      })
      .finally(() => this.setState({ isLoading: false }));
  };

  getDataForSubmit = () => {
    const {
      exercise,
      partyId,
      getDataForCopySubmit,
      serviceRequestIds
    } = this.props;

    getDataForCopySubmit(exercise.exerciseId, partyId, {
      serviceRequestIds: serviceRequestIds
    })
      .then(response => {
        if (response && response.success) {
          this.setState({ forSubmitData: response.payload });
        } else if (response) {
          Toast.warning(response.payload.message);
          onClose();
        }
      })
      .finally(() => this.setState({ isLoading: false }));
  };

  mapLocationForSubmit = (serviceRequest, where) => {
    if (where === 'to') {
      const isLocationSelected = !!serviceRequest.selectedLocation;
      return {
        regionId: isLocationSelected
          ? serviceRequest.selectedLocation.original.region
            ? serviceRequest.selectedLocation.original.region.regionId
            : null
          : serviceRequest.regionId,
        regionName: isLocationSelected
          ? serviceRequest.selectedLocation.original.region
            ? serviceRequest.selectedLocation.original.region.name
            : null
          : serviceRequest.regionName,
        locationName: isLocationSelected
          ? serviceRequest.selectedLocation.original.name
          : serviceRequest.locationName,
        locationComment: serviceRequest.locationComment,
        site: isLocationSelected
          ? serviceRequest.selectedLocation.original.site
          : serviceRequest.site,
        latitude: isLocationSelected
          ? serviceRequest.selectedLocation.original.latitude
          : serviceRequest.latitude,
        longitude: isLocationSelected
          ? serviceRequest.selectedLocation.original.longtitude
          : serviceRequest.longitude
      };
    }

    const isLocationFromSelected = !!serviceRequest.selectedLocationFrom;
    return {
      regionId: isLocationFromSelected
        ? serviceRequest.selectedLocationFrom.original.region
          ? serviceRequest.selectedLocationFrom.original.region.regionId
          : null
        : serviceRequest.regionIdFrom,
      regionName: isLocationFromSelected
        ? serviceRequest.selectedLocationFrom.original.region
          ? serviceRequest.selectedLocationFrom.original.region.name
          : null
        : serviceRequest.regionNameFrom,
      locationName: isLocationFromSelected
        ? serviceRequest.selectedLocationFrom.original.name
        : serviceRequest.locationNameFrom,
      locationComment: serviceRequest.locationCommentFrom,
      site: isLocationFromSelected
        ? serviceRequest.selectedLocationFrom.original.site
        : serviceRequest.siteFrom,
      latitude: isLocationFromSelected
        ? serviceRequest.selectedLocationFrom.original.latitude
        : serviceRequest.latitudeFrom,
      longitude: isLocationFromSelected
        ? serviceRequest.selectedLocationFrom.original.longtitude
        : serviceRequest.longitudeFrom
    };
  };

  onSubmit = (values, actions) => {
    const submitData = values.items.map(({ serviceRequest }) => {
      const location = this.mapLocationForSubmit(serviceRequest, 'to');
      const locationFrom = this.mapLocationForSubmit(serviceRequest);

      return {
        catalogItemId: !serviceRequest.isCustom
          ? serviceRequest.catalogItem.id
          : null,
        unitOfMeasureId: !serviceRequest.isCustom
          ? serviceRequest.catalogItem.unitOfMeasureId
          : serviceRequest.customCatalogItem.unitOfMeasureId,
        statementOfRequirementId: serviceRequest.statementOfRequirementId,
        unitId: serviceRequest.unitId,
        activityId: serviceRequest.activityId,
        requestedQuantity: serviceRequest.quantity,
        dateFrom: serviceRequest.dateFrom,
        dateTo: serviceRequest.dateTo,
        isCalculatedByDays: serviceRequest.isCalculatedByDays,
        comment: serviceRequest.comment,
        attachments: serviceRequest.attachments.map(
          attachment => attachment.attachmentId
        ),

        // to
        regionId: location.regionId,
        regionName: location.regionName,
        locationName: location.locationName,
        locationComment: location.locationComment,
        site: location.site,
        latitude: location.latitude,
        longitude: location.longitude,

        // from
        regionIdFrom: locationFrom.regionId,
        regionNameFrom: locationFrom.regionName,
        locationNameFrom: locationFrom.locationName,
        locationCommentFrom: locationFrom.locationComment,
        siteFrom: locationFrom.site,
        latitudeFrom: locationFrom.latitude,
        longitudeFrom: locationFrom.longitude,

        // transportation
        generalInformation: serviceRequest.generalInformation,
        lines: serviceRequest.lines,
        pickupDetails: serviceRequest.pickupDetails,
        deliveryDetails: serviceRequest.deliveryDetails,
        transportInformation: serviceRequest.transportInformation,

        // custom item
        annexId: serviceRequest.customCatalogItem.annexId,
        groupName: serviceRequest.customCatalogItem.groupName,
        serviceName: serviceRequest.customCatalogItem.name,
        serviceDescription: serviceRequest.customCatalogItem.description
      };
    });

    this.props
      .copyServiceRequests({
        exerciseId: this.props.exercise.exerciseId,
        partyId: this.props.partyId,
        serviceRequests: submitData
      })
      .then(response => {
        if (response && response.success) {
          Toast.success(this.props.t('Service requests copied successfully'));
          this.props.onClose();
        } else if (response && response.payload) {
          Toast.warning(response.payload.message);
        } else {
          Toast.error(this.props.t('Failed to copy service requests'));
        }
      });
  };

  toUomsEntity(uoms) {
    return uoms.map(uom => {
      return {
        title: uom.unitOfMeasureCode,
        value: uom.unitOfMeasureId
      };
    });
  }

  toActivityEntity(activities) {
    return activities.map(activity => {
      return {
        name: activity.name,
        activityId: activity.activityId,
        isDefault: activity.isDefault
      };
    });
  }

  toUnitsEntity(units) {
    return units.map(unit => {
      return {
        title: unit.title,
        value: unit.unitId
      };
    });
  }

  getUnits = units => {
    const newUnits = this.flattenUnitsTree(units).map(unit => {
      return {
        title: unit.unitName,
        unitId: unit.unitId
      };
    });

    return this.toUnitsEntity(newUnits);
  };

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    const units = array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.unitName}`,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);

    return units;
  }

  getExerciseCurrency = () => {
    if (this.state.forSubmitData.exerciseCurrency) {
      return this.state.forSubmitData.exerciseCurrency.name;
    }

    return '';
  };

  toAutocompleteEntity(data) {
    return data.map(item => ({
      value: item.code,
      title: item.name,
      original: item
    }));
  }

  isItemSelected = () => {
    if (!this.formRef.current) {
      return false;
    }
    const formHasItems = this.formRef.current.values.items.length > 0;
    return !!this.state.selectedServiceRequestNumber && formHasItems;
  };

  getSelectedItem = values => {
    if (!this.isItemSelected() || !values) {
      return {};
    }

    const item = values.items.find(
      x =>
        x.serviceRequest.requestNumber ===
        this.state.selectedServiceRequestNumber
    );

    return item ? item.serviceRequest : {};
  };

  onGridRowClick = (dataItem, validateForm) => {
    if (
      this.state.selectedServiceRequestNumber ===
      dataItem.serviceRequest.requestNumber
    ) {
      return;
    }

    this.setState(
      {
        selectedServiceRequestNumber: dataItem.serviceRequest.requestNumber
      },
      () => {
        this.props.clearCalculateByDays();
        validateForm();
      }
    );
  };

  toValidationErrorsEntity = errors => {
    return (
      <div>
        {errors.map(error => (
          <div>{SERVICE_REQUEST_COPY_VALIDATION_ERRORS[error]}</div>
        ))}
      </div>
    );
  };

  getRowIndex = (values, requestNumber) => {
    const index = values.items.findIndex(
      x => x.serviceRequest.requestNumber === requestNumber
    );

    return index !== -1 ? index : 0;
  };

  getDefaultUom = values => {
    if (!this.isItemSelected()) {
      return {};
    }

    return (
      objectPath
        .get(this.getSelectedItem(values).catalogItem, 'unitsOfMeasure', [])
        .find(uom => uom.isDefault) || {}
    );
  };

  calculateByDays = values => {
    if (values.items.length < 1) {
      return;
    }

    const sr =
      values.items[
        this.getRowIndex(values, this.state.selectedServiceRequestNumber)
      ].serviceRequest;

    const data = CalculateByDays.calculate(sr, sr.catalogItem, true);

    if (data) {
      this.props.calculateByDays(data);
    }
  };

  onLocationQueryChange = query => {
    if (this.locationQueryTimeout) {
      clearTimeout(this.locationQueryTimeout);
    }

    this.locationQueryTimeout = setTimeout(() => {
      this.props.queryLocations(query, this.props.exercise.exerciseId);
    }, 300);
  };

  getInitialLocation = (sr, which) => {
    if (which === 'to') {
      const isLocationSelected = !!sr.selectedLocation;
      return {
        original: {
          latitude: isLocationSelected
            ? sr.selectedLocation.original.latitude
            : sr.latitude,
          longtitude: isLocationSelected
            ? sr.selectedLocation.original.longtitude
            : sr.longitude,
          name: isLocationSelected
            ? sr.selectedLocation.original.name
            : sr.locationName,
          region: {
            regionId: isLocationSelected
              ? sr.selectedLocation.original.region
                ? sr.selectedLocation.original.region.regionId
                : null
              : sr.regionId,
            name: isLocationSelected
              ? sr.selectedLocation.original.region
                ? sr.selectedLocation.original.region.name
                : null
              : sr.regionName
          },
          site: isLocationSelected ? sr.selectedLocation.original.site : sr.site
        }
      };
    }

    const isLocationFromSelected = !!sr.selectedLocationFrom;
    return {
      original: {
        latitude: isLocationFromSelected
          ? sr.selectedLocationFrom.original.latitude
          : sr.latitudeFrom,
        longtitude: isLocationFromSelected
          ? sr.selectedLocationFrom.original.longtitude
          : sr.longitudeFrom,
        name: isLocationFromSelected
          ? sr.selectedLocationFrom.original.name
          : sr.locationNameFrom,
        region: {
          regionId: isLocationFromSelected
            ? sr.selectedLocationFrom.original.region
              ? sr.selectedLocationFrom.original.region.regionId
              : null
            : sr.regionIdFrom,
          name: isLocationFromSelected
            ? sr.selectedLocationFrom.original.region
              ? sr.selectedLocationFrom.original.region.name
              : null
            : sr.regionNameFrom
        },
        site: isLocationFromSelected
          ? sr.selectedLocationFrom.original.site
          : sr.siteFrom
      }
    };
  };

  handleFileUpload = value => {
    return this.props.uploadAttachments({ attachments: value });
  };

  removeServiceRequest = requestNumber => {
    const newValues = this.formRef.current.values.items.filter(
      x => x.serviceRequest.requestNumber !== requestNumber
    );
    if (this.state.selectedServiceRequestNumber === requestNumber) {
      const anyItemsLeft = newValues.length > 0;
      if (anyItemsLeft) {
        this.setState({
          selectedServiceRequestNumber:
            newValues[0].serviceRequest.requestNumber
        });
      } else {
        this.setState({ selectedServiceRequestNumber: null });
      }
    }
    this.formRef.current.setValues({
      items: newValues
    });
  };

  isSubmitDisabled = () => {
    if (!this.formRef.current || this.formRef.current.values.items < 1) {
      return true;
    }

    const errors = this.formRef.current.values.items.flatMap(
      item => item.errors
    );
    return errors.length > 0;
  };

  getCatalogItem = sr => {
    return sr.isCustom ? sr.customCatalogItem : sr.catalogItem;
  };

  getAnnexOptions = () => {
    const { t } = this.props;
    const annexes = [...this.state.forSubmitData.annexes];

    if (annexes.length) {
      return []
        .concat(
          [
            {
              annexName: t('Select annex'),
              annexId: ''
            }
          ],
          annexes
        )
        .map(annex => {
          return (
            <option key={ShortId.generate()} value={annex.annexId}>
              {annex.annexName}
            </option>
          );
        });
    }

    return (
      <option value="">{t('There are no Annexes for this Exercise')}</option>
    );
  };

  setAnnexValue = (event, setFieldValue, values) => {
    const { groups } = this.state.forSubmitData;
    if (groups) {
      const currentGroup = groups.filter(
        group => group.groupName === event.target.value
      )[0];

      if (currentGroup && currentGroup.annexId) {
        setFieldValue(
          `items[${this.getRowIndex(
            values,
            this.state.selectedServiceRequestNumber
          )}].serviceRequest.customCatalogItem.annexId`,
          currentGroup.annexId
        );
      }
    }
  };

  getGroupOptions = annexId => {
    const { t } = this.props;
    const groups = [...this.state.forSubmitData.groups];

    if (groups.length) {
      const filteredGroups = annexId
        ? groups.filter(group => group.annexId === annexId)
        : groups;

      const placeholder = (
        <option key={ShortId.generate()} value="">
          {t('Select group')}
        </option>
      );

      const options = filteredGroups.map(group => {
        return (
          <option key={ShortId.generate()} value={group.groupName}>
            {group.groupName}
          </option>
        );
      });
      return [placeholder, options];
    }

    return (
      <option value="">{t('There are no Groups for this Exercise')}</option>
    );
  };

  renderCustomServiceRequestItemInfo(setFieldValue, t, values) {
    const annexId =
      values.items[
        this.getRowIndex(values, this.state.selectedServiceRequestNumber)
      ].serviceRequest.customCatalogItem.annexId;

    return (
      <div className="copy-service-requests-form--custom-item">
        <div className="service-request-custom--row">
          <Field
            name={`items[${this.getRowIndex(
              values,
              this.state.selectedServiceRequestNumber
            )}].serviceRequest.customCatalogItem.annexId`}
            component={Dropdown}
            setFieldValue={setFieldValue}
            label={t('Annex')}
            placeholder={t('Select annex')}
            onChange={() => setFieldValue('groupName', null)}
          >
            {this.getAnnexOptions()}
          </Field>
          <Field
            name={`items[${this.getRowIndex(
              values,
              this.state.selectedServiceRequestNumber
            )}].serviceRequest.customCatalogItem.groupName`}
            component={Dropdown}
            setFieldValue={setFieldValue}
            label={t('Service group')}
            placeholder={t('Select group')}
            onChange={event => {
              setFieldValue('groupName', event.target.value);
              if (!annexId) {
                this.setAnnexValue(event, setFieldValue, values);
              }
            }}
          >
            {this.getGroupOptions(annexId)}
          </Field>
          {[...this.state.forSubmitData.annexes].find(
            x => annexId == x.annexId && x.annexInformation
          ) && (
            <div className="informationTooltip">
              <InformationToolTip
                toolTipInformation={
                  [...this.state.forSubmitData.annexes].find(
                    x => annexId == x.annexId
                  ).annexInformation
                }
              ></InformationToolTip>
            </div>
          )}
        </div>
        <div>
          <Field
            name={`items[${this.getRowIndex(
              values,
              this.state.selectedServiceRequestNumber
            )}].serviceRequest.customCatalogItem.name`}
            component={Input}
            placeholder={t('Name')}
          >
            {t('Service name')}
          </Field>
        </div>
        <div>
          <Field
            name={`items[${this.getRowIndex(
              values,
              this.state.selectedServiceRequestNumber
            )}].serviceRequest.customCatalogItem.description`}
            component={Textarea}
            placeholder={t('Description')}
            label={t('Service description')}
          />
        </div>
      </div>
    );
  }

  setInvalidRows = (setFieldValue, errors, values) => {
    const items = errors.items;
    const modifiedItems = [];
    if (items && items.length > 0) {
      items.forEach((item, index) => {
        setFieldValue(`items[${index}].isValid`, item == null);
        modifiedItems.push(index);
      });
    }

    if (values) {
      values.items.forEach((item, index) => {
        if (!modifiedItems.includes(index)) {
          setFieldValue(`items[${index}].isValid`, true);
        }
      });
    }
  };

  handleInvalidFieldsOnChange = (values, setFieldValue) => {
    if (!values || values.length < 1) {
      return;
    }

    const item =
      values.items[
        this.getRowIndex(values, this.state.selectedServiceRequestNumber)
      ];
    const errors = item.errors;

    if (errors.length < 1) {
      return;
    }

    const errorChecks = [
      {
        code: 1,
        check: function(item) {
          return !!item.serviceRequest.unitId;
        }
      },
      {
        code: 2,
        check: function(item) {
          return !!item.serviceRequest.activityId;
        }
      },
      {
        code: 3,
        check: function(item) {
          var sr = item.serviceRequest;
          return (
            sr &&
            sr.selectedLocationFrom &&
            sr.selectedLocationFrom.original &&
            !!sr.selectedLocationFrom.original.name
          );
        }
      },
      {
        code: 4,
        check: function(item) {
          var sr = item.serviceRequest;
          return (
            sr &&
            sr.selectedLocation &&
            sr.selectedLocation.original &&
            !!sr.selectedLocation.original.name
          );
        }
      }
    ];

    for (var i = 0; i < errorChecks.length; i++) {
      var code = errorChecks[i].code;
      var check = errorChecks[i].check;
      if (check(item) && errors.indexOf(code) !== -1) {
        var newErrors = errors.filter(x => x !== code);
        setFieldValue(
          'items[' +
            this.getRowIndex(values, this.state.selectedServiceRequestNumber) +
            '].errors',
          newErrors
        );
      }
    }
  };

  render() {
    const { isOpen, onClose, t, exercise, isLoading } = this.props;

    return (
      <Dialog
        title={t('Copy service requests to exercise ') + exercise.title}
        isOpen={isOpen}
        onClose={() =>
          Confirm(t('Confirm'), t(`Are you sure you want to cancel?`), () => {
            onClose();
          })
        }
        onOpening={() => {
          this.setState({ isLoading: true });
          this.getDataForSubmit();
          this.getServiceRequests();
        }}
        onClosing={this.props.clearCalculateByDays}
        className={'copy-service-requests-dialog'}
      >
        <>
          {this.state.validatedServiceRequests.length < 1 ? (
            <Loader />
          ) : (
            <Formik
              enableReinitialize={true}
              initialValues={{
                items: this.state.validatedServiceRequests
              }}
              onSubmit={this.onSubmit}
              validationSchema={this.validationSchema}
              innerRef={this.formRef}
              validate={values => this.calculateByDays(values)}
            >
              {({ values, setFieldValue, validateForm, handleSubmit }) => {
                const selectedItem = this.getSelectedItem(values);
                const catalogItem = this.getCatalogItem(selectedItem);
                this.handleInvalidFieldsOnChange(values, setFieldValue);

                return (
                  <Form className="copy-service-requests-form">
                    <div className="copy-service-requests-form--panels">
                      <Panel
                        withArrow
                        heading={{
                          title: t('Service requests'),
                          number: 1
                        }}
                      >
                        <Grid
                          sortable={true}
                          data={values.items.map(item => ({
                            ...item,
                            selected:
                              item.serviceRequest.requestNumber ===
                              this.state.selectedServiceRequestNumber
                          }))}
                          selectedField="selected"
                          onRowClick={({ dataItem }) =>
                            this.onGridRowClick(
                              dataItem,
                              setFieldValue,
                              validateForm
                            )
                          }
                          className="copy-service-requests-form--panels--grid"
                          rowRender={(row, props) => {
                            const customClassName = [
                              row.props.className,
                              props.dataItem.isValid === false
                                ? 'copy-service-requests-form--panels--grid--row'
                                : ''
                            ]
                              .join(' ')
                              .trim();

                            return React.cloneElement(row, {
                              className: customClassName
                            });
                          }}
                        >
                          <GridColumn
                            width={150}
                            field="serviceRequest.requestNumber"
                            title={'Request number'}
                          />
                          <GridColumn
                            title={'Item'}
                            sortable={false}
                            cell={({ dataItem }) => {
                              return (
                                <td>
                                  {dataItem.serviceRequest.isCustom
                                    ? objectPath.get(
                                        dataItem.serviceRequest
                                          .customCatalogItem,
                                        'code',
                                        ''
                                      ) +
                                      ' ' +
                                      dataItem.serviceRequest.customCatalogItem
                                        .name
                                    : dataItem.serviceRequest.catalogItem.code +
                                      ' ' +
                                      dataItem.serviceRequest.catalogItem.name}
                                </td>
                              );
                            }}
                          />
                          <GridColumn
                            width={50}
                            cell={({ dataItem }) => (
                              <td>
                                {dataItem.errors.length > 0 && (
                                  <Tooltip
                                    tooltip={this.toValidationErrorsEntity(
                                      dataItem.errors
                                    )}
                                    position="left"
                                  >
                                    <BpIcon
                                      icon="warning-sign"
                                      color="#b50026"
                                    />
                                  </Tooltip>
                                )}
                              </td>
                            )}
                          />
                          <GridColumn
                            width={50}
                            cell={({ dataItem }) => (
                              <td
                                onClick={() =>
                                  Confirm(
                                    t('Confirm'),
                                    t(
                                      'Are you sure you want to remove this service request?'
                                    ),
                                    () =>
                                      this.removeServiceRequest(
                                        dataItem.serviceRequest.requestNumber
                                      )
                                  )
                                }
                                className="copy-service-requests-form--button"
                              >
                                <BpIcon icon="trash" color="#046fe2" />
                              </td>
                            )}
                          />
                        </Grid>
                      </Panel>
                      <Panel
                        heading={{
                          title: t('Information'),
                          number: 2
                        }}
                      >
                        {this.formRef.current && this.isItemSelected() && (
                          <div className="service-request--panel">
                            <H5>{t('Item information')}</H5>
                            {!selectedItem.isCustom ? (
                              <>
                                <InfoPanel
                                  key={objectPath.get(catalogItem, 'id')}
                                  title={objectPath.get(
                                    catalogItem,
                                    'name',
                                    '-'
                                  )}
                                  code={objectPath.get(catalogItem, 'code', '')}
                                  toolTipInformation={objectPath.get(
                                    catalogItem,
                                    'annexInformation',
                                    ''
                                  )}
                                >
                                  {objectPath.get(
                                    catalogItem,
                                    'description',
                                    '-'
                                  )}
                                </InfoPanel>

                                <div className="service-request--panel-info">
                                  <div>
                                    {t('Item price')}:{' '}
                                    <span>
                                      <Money
                                        amount={objectPath.get(
                                          catalogItem,
                                          'rateFrom',
                                          ''
                                        )}
                                      />{' '}
                                      -{' '}
                                      <Money
                                        amount={objectPath.get(
                                          catalogItem,
                                          'rateTo',
                                          ''
                                        )}
                                      />{' '}
                                      {this.getExerciseCurrency()}
                                      {' per '}
                                      {
                                        this.getDefaultUom(values)
                                          .unitOfMeasureCode
                                      }
                                    </span>
                                  </div>

                                  {objectPath
                                    .get(catalogItem, 'attachments', [])
                                    .map(file => (
                                      <File
                                        key={ShortId.generate()}
                                        name={file.name}
                                        attachmentId={file.attachmentId}
                                      />
                                    ))}
                                </div>
                              </>
                            ) : (
                              this.renderCustomServiceRequestItemInfo(
                                setFieldValue,
                                t,
                                values
                              )
                            )}

                            <H5>{t('Delivery information')}</H5>
                            <DeliveryInformation
                              showTotalEstimates={selectedItem.quantity !== ''}
                              uoms={this.toUomsEntity(
                                objectPath.get(
                                  catalogItem,
                                  'unitsOfMeasure',
                                  []
                                )
                              )}
                              activities={this.toActivityEntity(
                                this.state.forSubmitData.activities || []
                              )}
                              units={this.getUnits(
                                this.state.forSubmitData.units
                              )}
                              setFieldValue={setFieldValue}
                              results={this.props.calculateByDaysResults}
                              checked={selectedItem.isCalculatedByDays}
                              values={selectedItem}
                              activityInForm
                              showCurrency={true}
                              showPlaceholderOption={true}
                              exerciseCurrency={this.getExerciseCurrency()}
                              schema={{
                                unitId: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.unitId`,
                                dateFrom: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.dateFrom`,
                                quantity: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.quantity`,
                                unitOfMeasureId: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.${
                                  selectedItem.isCustom
                                    ? 'customCatalogItem'
                                    : 'catalogItem'
                                }.unitOfMeasureId`,
                                dateTo: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.dateTo`,
                                isCalculatedByDays: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.isCalculatedByDays`,
                                activityId: `items[${this.getRowIndex(
                                  values,
                                  this.state.selectedServiceRequestNumber
                                )}].serviceRequest.activityId`
                              }}
                            />

                            {selectedItem.isTransportation === false && (
                              <>
                                <H5>{t('Location details')}</H5>
                                <LocationDetails
                                  setFieldValue={setFieldValue}
                                  locationItems={this.toAutocompleteEntity(
                                    this.props.locationQuery
                                  )}
                                  onQueryChange={this.onLocationQueryChange}
                                  initialLocation={this.getInitialLocation(
                                    selectedItem,
                                    'from'
                                  )}
                                  defaultQuery={
                                    this.getInitialLocation(
                                      selectedItem,
                                      'from'
                                    ).original.name
                                  }
                                  schema={{
                                    location: `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.selectedLocationFrom`,
                                    locationComment: `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.locationCommentFrom`
                                  }}
                                />
                              </>
                            )}

                            {selectedItem.isTransportation === true && (
                              <>
                                <H5>{t('Pickup location')}</H5>
                                <LocationDetails
                                  setFieldValue={setFieldValue}
                                  locationItems={this.toAutocompleteEntity(
                                    this.props.locationQuery
                                  )}
                                  onQueryChange={this.onLocationQueryChange}
                                  isExpandable={true}
                                  initialLocation={this.getInitialLocation(
                                    selectedItem,
                                    'from'
                                  )}
                                  defaultQuery={
                                    this.getInitialLocation(
                                      selectedItem,
                                      'from'
                                    ).original.name
                                  }
                                  schema={{
                                    location: `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.selectedLocationFrom`,
                                    locationComment: `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.locationCommentFrom`
                                  }}
                                />
                                <H5>{t('Delivery location')}</H5>
                                <LocationDetails
                                  setFieldValue={setFieldValue}
                                  locationItems={this.toAutocompleteEntity(
                                    this.props.locationQuery
                                  )}
                                  onQueryChange={this.onLocationQueryChange}
                                  isExpandable={true}
                                  initialLocation={this.getInitialLocation(
                                    selectedItem,
                                    'to'
                                  )}
                                  defaultQuery={
                                    this.getInitialLocation(selectedItem, 'to')
                                      .original.name
                                  }
                                  schema={{
                                    location: `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.selectedLocation`,
                                    locationComment: `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.locationComment`
                                  }}
                                />
                              </>
                            )}

                            <H5>{t('Additional details')}</H5>
                            <Field
                              name={`items[${this.getRowIndex(
                                values,
                                this.state.selectedServiceRequestNumber
                              )}].serviceRequest.comment`}
                              setFieldValue={setFieldValue}
                              placeholder={t('Describe additional details')}
                              label={t('Comment')}
                              component={Textarea}
                            />

                            <div className="documents-section">
                              <DropZone
                                value={objectPath.get(
                                  values.items[
                                    this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )
                                  ].serviceRequest.attachments,
                                  []
                                )}
                                onSelect={files => {
                                  this.handleFileUpload(files).then(
                                    response => {
                                      setFieldValue(
                                        `items[${this.getRowIndex(
                                          values,
                                          this.state
                                            .selectedServiceRequestNumber
                                        )}].serviceRequest.attachments`,
                                        values.items[
                                          this.getRowIndex(
                                            values,
                                            this.state
                                              .selectedServiceRequestNumber
                                          )
                                        ].serviceRequest.attachments.concat(
                                          response.payload
                                        )
                                      );
                                    }
                                  );
                                }}
                                onRemove={(file, list) => {
                                  setFieldValue(
                                    `items[${this.getRowIndex(
                                      values,
                                      this.state.selectedServiceRequestNumber
                                    )}].serviceRequest.attachments`,
                                    list
                                  );
                                }}
                                key={ShortId.generate()}
                              />
                            </div>
                          </div>
                        )}
                      </Panel>
                    </div>
                    <div className="copy-service-requests-form--buttons">
                      <GhostButton
                        onClick={() => {
                          Confirm(
                            t('Confirm'),
                            t(`Are you sure you want to cancel?`),
                            onClose
                          );
                        }}
                        type="button"
                        large
                        disabled={isLoading}
                      >
                        {t('Cancel')}
                      </GhostButton>
                      <RaisedButton
                        type="button"
                        disabled={values.items.length < 1 || isLoading}
                        large
                        onClick={() => {
                          Confirm(
                            t('Confirm'),
                            t(
                              `Are you sure you want to copy selected services to exercise ${this.props.exercise.title}?`
                            ),
                            async () => {
                              handleSubmit();
                              const errors = await validateForm();
                              this.setInvalidRows(
                                setFieldValue,
                                errors,
                                values
                              );
                            }
                          );
                        }}
                      >
                        {t('Confirm All')}
                      </RaisedButton>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          )}
        </>
      </Dialog>
    );
  }
}

CopyServiceRequestsDialog.propTypes = {
  isOpen: PropTypes.bool,
  t: PropTypes.func,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  exercise: PropTypes.object,
  validateServiceRequestsForCopy: PropTypes.func,
  partyId: PropTypes.string,
  serviceRequestIds: PropTypes.array,
  getDataForCopySubmit: PropTypes.func
};

const isLoading = createLoadingSelector([
  'ServiceRequests/CopyServiceRequests',
  'ServiceRequests/GetDataForCopySubmit',
  'ServiceRequests/ValidateServiceRequestsForCopy'
]);

const mapStateToProps = state => ({
  locationQuery: state.locations.query,
  calculateByDaysResults: state.serviceRequests.calculateByDays,
  isLoading: isLoading(state)
});

const mapDispatchToProps = {
  validateServiceRequestsForCopy,
  getDataForCopySubmit,
  queryLocations,
  uploadAttachments,
  calculateByDays,
  clearCalculateByDays,
  copyServiceRequests
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(CopyServiceRequestsDialog));
